import cognitoService from './cognitoService';

const apiEndpoint = () =>
  import.meta.env.VITE_API_ENDPOINT || import.meta.env.REACT_APP_API_ENDPOINT;

const authHeaders = () => {
  const token = cognitoService.getIdToken();
  if (!token) throw new Error('User is not authenticated');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

/**
 * Fetch all user usage records from HomeschoolUserUsage.
 * The existing /usage endpoint is called without a username to get the full scan.
 * Falls back to a best-effort scan via the history endpoint if not available.
 */
export const fetchAllUsersUsage = async () => {
  const response = await fetch(`${apiEndpoint()}/usage?admin=true`, {
    method: 'GET',
    headers: authHeaders(),
    mode: 'cors',
  });

  if (response.status === 403) throw new Error('Admin access required.');
  if (!response.ok) throw new Error(`Failed to fetch usage data: ${response.status}`);
  return response.json();
};

/**
 * Fetch activity summary for the last 30 days across all users.
 */
export const fetchRecentActivitySummary = async () => {
  const since = new Date();
  since.setDate(since.getDate() - 30);
  const sinceISO = since.toISOString();

  const response = await fetch(
    `${apiEndpoint()}/history?admin=true&since=${encodeURIComponent(sinceISO)}&limit=1000`,
    {
      method: 'GET',
      headers: authHeaders(),
      mode: 'cors',
    }
  );

  if (response.status === 403) throw new Error('Admin access required.');
  if (!response.ok) throw new Error(`Failed to fetch activity data: ${response.status}`);
  return response.json();
};

/**
 * Derive dashboard stats from raw API responses.
 *
 * @param {Object} usageData   – response from fetchAllUsersUsage
 * @param {Object} activityData – response from fetchRecentActivitySummary
 */
export const computeDashboardStats = (usageData, activityData) => {
  // ── Users ────────────────────────────────────────────────────────────────
  const users = Array.isArray(usageData?.users)
    ? usageData.users
    : Array.isArray(usageData?.items)
    ? usageData.items
    : [];

  const totalUsers = users.length;

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const activeUsersThisMonth = users.filter((u) => {
    const d = u.last_activity_date || u.lastActivityDate;
    return d && new Date(d) >= thirtyDaysAgo;
  }).length;

  const tierCounts = users.reduce((acc, u) => {
    const tier = u.subscription_tier || u.subscriptionTier || 'free';
    acc[tier] = (acc[tier] || 0) + 1;
    return acc;
  }, {});

  // ── Activities ───────────────────────────────────────────────────────────
  const activities = Array.isArray(activityData?.activities)
    ? activityData.activities
    : [];

  const activitiesThisMonth = activities.length;

  // Count per subject
  const subjectCounts = activities.reduce((acc, a) => {
    const subject = a.subject || a.metadata?.subject || 'Unknown';
    acc[subject] = (acc[subject] || 0) + 1;
    return acc;
  }, {});

  // Count per grade
  const gradeCounts = activities.reduce((acc, a) => {
    const grade = a.gradeLevel || a.metadata?.gradeLevel || 'Unknown';
    acc[grade] = (acc[grade] || 0) + 1;
    return acc;
  }, {});

  // Activities per day (last 30 days) for the sparkline
  const dailyCounts = {};
  activities.forEach((a) => {
    const day = (a.createdAt || '').substring(0, 10);
    if (day) dailyCounts[day] = (dailyCounts[day] || 0) + 1;
  });

  const dailySeries = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    const key = d.toISOString().substring(0, 10);
    return { date: key, count: dailyCounts[key] || 0 };
  });

  return {
    totalUsers,
    activeUsersThisMonth,
    tierCounts,
    activitiesThisMonth,
    subjectCounts,
    gradeCounts,
    dailySeries,
  };
};
